// DS: COF0.2
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { BiData } from '@wix/da-bi/pkg/events';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import ListItemRadioButton from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemRadioButton';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { AI_TOPIC_ID } from '../redux/topic/constants';
import useFetchedTopicWatchLevel from './useFetchedTopicWatchLevel';
import s from './AISuppressionSettingsModal.scss';
// NOTE: we aren't allowing a change TO 'watch' from this modal
// or allowing changing from 'watching' to 'not-watching'
const SETTABLE_WATCH_LEVELS = [
    'not-watching',
    'suppressed'
];
const AI_LEVEL_CHANGE_ACTION_DATA = {
    necoid: AI_TOPIC_ID,
    source: 'ai_suppression_modal'
};
/** a modal to change the watch/suppression level of the AI topic.
 *  Fetches the current value when mounted */ export const AISuppressionSettingsModal = ({ unwatchTopic, suppressTopic, requestClose })=>{
    const { t } = useTranslation();
    const { fetchTopicWatchLevel, topicWatchLevel: aiSuppressionLevel, isLoading, error } = useFetchedTopicWatchLevel(AI_TOPIC_ID);
    useEffect(()=>{
        fetchTopicWatchLevel();
    });
    const [currentSettingInModal, updateSettingInModal] = useState(aiSuppressionLevel);
    useEffect(()=>{
        const normalized = aiSuppressionLevel === 'suppressed' ? 'suppressed' : 'not-watching';
        updateSettingInModal(normalized);
    }, [
        aiSuppressionLevel
    ]);
    return /*#__PURE__*/ React.createElement(Modal, {
        contentClassName: classnames(isLoading && s['loading'], s['modal-content']),
        heading: t('AISuppressionSettingsModal.title'),
        onRequestClose: requestClose,
        isOpen: true,
        footerRight: [
            /*#__PURE__*/ React.createElement(TextButton, {
                key: "cancel",
                size: "large",
                variant: "main",
                onClick: requestClose
            }, !error ? t('AISuppressionSettingsModal.buttons.cancel') : t('AISuppressionSettingsModal.error.ok')),
            !error && /*#__PURE__*/ React.createElement(BrandedButton, {
                key: "save",
                size: "large",
                variant: "brand-green",
                disabled: isLoading || !!error,
                biData: BiData({
                    evid: 308,
                    view_mode: currentSettingInModal === 'suppressed' ? 'suppress' : 'show'
                }),
                onClick: ()=>{
                    const updatedLevel = getUpdatedLevel(currentSettingInModal, aiSuppressionLevel);
                    if (updatedLevel) {
                        const action = updatedLevel === 'suppressed' ? suppressTopic : unwatchTopic;
                        action(AI_LEVEL_CHANGE_ACTION_DATA, ()=>{
                            typeof window !== 'undefined' && window.location.reload();
                        });
                    } else {
                        requestClose();
                    }
                }
            }, t('AISuppressionSettingsModal.buttons.save'))
        ]
    }, isLoading ? /*#__PURE__*/ React.createElement(LoadingIndicator, null) : error ? /*#__PURE__*/ React.createElement(Heading, {
        className: s['error']
    }, t('AISuppressionSettingsModal.error')) : /*#__PURE__*/ React.createElement("div", {
        role: "radiogroup",
        "aria-label": t('AISuppressionSettingsModal.title')
    }, SETTABLE_WATCH_LEVELS.map((setting)=>{
        return /*#__PURE__*/ React.createElement(ListItemRadioButton, {
            key: setting,
            className: s['radio-button'],
            value: setting,
            name: "content_filter",
            label: /*#__PURE__*/ React.createElement("span", {
                className: s['radio-label']
            }, t(`AISuppressionSettingsModal.${setting}.label`)),
            subLabel: t(`AISuppressionSettingsModal.${setting}.sublabel`),
            checked: currentSettingInModal === setting,
            onChange: ()=>updateSettingInModal(setting)
        });
    })));
};
AISuppressionSettingsModal.displayName = 'AISuppressionSettingsModal';
export default AISuppressionSettingsModal;
function getUpdatedLevel(newLevel, oldLevel) {
    // logic is a bit convoluted because we need to reduce 3 possible
    // settings down to 2 for the form
    // fetch might not have returned properly yet
    if (!oldLevel || !newLevel) {
        return undefined;
    }
    // no change needed if levels are the same
    if (newLevel === oldLevel) {
        return undefined;
    }
    // only:
    // - suppression --> unwatch
    // - unwatch OR watch --> suppression
    // should trigger change
    //
    // modal should do nothing if AI was watching and user
    //   kept or selected not-watching (we don't want to inadvertently
    //   overwrite watch)
    if (oldLevel === 'watching' && newLevel === 'not-watching') {
        return undefined;
    }
    return newLevel === 'suppressed' ? 'suppressed' : 'not-watching';
}

//# sourceMappingURL=AISuppressionSettingsModal.js.map